/**
 * Settings — master data, users, rate, defaults, backup, audit log.
 *
 * Functional spec: `qt_poc/settings_qt.py`, minus the Qt-only parts the plan
 * says to skip (theme files, table-layout reset, DB-location picker — the
 * server's data directory is set by environment on a container).
 *
 * The role gating here is visibility only; every one of these endpoints is
 * admin- or editor-guarded server-side, and the DAO guards sit behind that.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Badge, Button, Card, Group, NumberInput,
  PasswordInput, Paper, Select, SimpleGrid, Stack, Tabs, Text,
  TextInput, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type {
  AuditRow, Backup, Defaults, Dealer, Rate, User,
} from '../api/types'
import { ROLE_LABEL_HE, useAuth } from '../auth/AuthContext'
import { MIN_PASSWORD_LENGTH, passwordProblem } from '../auth/password'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { ResetPasswordModal } from '../components/PasswordModals'
import { StatusBadge } from '../components/StatusBadge'
import { EM_DASH, fmtDateTime } from '../format'
import { CODE_STYLE, FILL_PANEL, FILL_REMAINING } from '../theme'
import { confirmDelete } from '../components/confirm'
import { CatalogImportModal } from './CatalogImport'
import { ManufacturerAliases, MasterDataLists } from './MasterDataLists'

export function SettingsPage() {
  const { isAdmin } = useAuth()
  return (
    <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
      <Title order={2}>הגדרות</Title>
      {/* Same shape as `InventoryPage`'s tabs: making `Tabs` itself a flex
          column with a real height is what lets a grid-bearing panel below
          (`dealers`/`users`/`audit`) fill the rest instead of sizing to a
          fixed vh guess. The form-only tabs (`general`/`master`/`data`) do
          not opt into that — they just render at their own content height
          inside the same panel, same as before this existed. */}
      <Tabs
        defaultValue="general"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Tabs.List>
          <Tabs.Tab value="general">כללי</Tabs.Tab>
          <Tabs.Tab value="master">נתוני אב</Tabs.Tab>
          <Tabs.Tab value="dealers">ספקים</Tabs.Tab>
          {isAdmin && <Tabs.Tab value="users">משתמשים</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="data">נתונים</Tabs.Tab>}
          {isAdmin && <Tabs.Tab value="audit">יומן פעולות</Tabs.Tab>}
        </Tabs.List>

        <Tabs.Panel value="general" pt="md" style={FILL_PANEL}><GeneralTab /></Tabs.Panel>
        <Tabs.Panel value="master" pt="md" style={FILL_PANEL}><MasterDataTab /></Tabs.Panel>
        <Tabs.Panel value="dealers" pt="md" style={FILL_PANEL}><DealersTab /></Tabs.Panel>
        {isAdmin && <Tabs.Panel value="users" pt="md" style={FILL_PANEL}><UsersTab /></Tabs.Panel>}
        {isAdmin && <Tabs.Panel value="data" pt="md" style={FILL_PANEL}><DataTab /></Tabs.Panel>}
        {isAdmin && <Tabs.Panel value="audit" pt="md" style={FILL_PANEL}><AuditTab /></Tabs.Panel>}
      </Tabs>
    </Stack>
  )
}

function GeneralTab() {
  const qc = useQueryClient()
  const { canEdit, isAdmin } = useAuth()
  const rate = useQuery({ queryKey: ['rate'], queryFn: () => api.get<Rate>('/settings/rate') })
  const defaults = useQuery({ queryKey: ['defaults'], queryFn: () => api.get<Defaults>('/settings/defaults') })
  const [rateValue, setRateValue] = useState<number | ''>('')
  const [vat, setVat] = useState<number | ''>('')
  const [ship, setShip] = useState<number | ''>('')

  useEffect(() => { if (rate.data) setRateValue(rate.data.rate) }, [rate.data])
  useEffect(() => {
    if (defaults.data) { setVat(defaults.data.vat_pct); setShip(defaults.data.shipping_pct) }
  }, [defaults.data])

  const saveRate = useMutation({
    mutationFn: (value: number) => api.put<Rate>('/settings/rate', { rate: value }),
    onSuccess: () => {
      void qc.invalidateQueries()
      notifications.show({ message: 'שער החליפין עודכן', color: 'green' })
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'העדכון נכשל' }),
  })

  const saveDefaults = useMutation({
    mutationFn: (value: Defaults) => api.put<Defaults>('/settings/defaults', value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['defaults'] })
      notifications.show({ message: 'ברירות המחדל עודכנו', color: 'green' })
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'העדכון נכשל' }),
  })

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Card withBorder padding="md">
        <Title order={5} mb="xs">שער חליפין</Title>
        <Text size="xs" c="dimmed" mb="sm">
          משפיע על כל סכום שטרם הוקפא. הזמנות וניפוקים שכבר בוצעו שומרים על
          השער שהוקפא בהם ואינם משתנים.
        </Text>
        <Group align="flex-end">
          <NumberInput
            label="₪ לדולר"
            hideControls
            min={0.01}
            decimalScale={4}
            value={rateValue}
            onChange={(v) => setRateValue(typeof v === 'number' ? v : '')}
            disabled={!canEdit}
            style={{ maxWidth: 160 }}
          />
          <Button
            disabled={!canEdit || rateValue === '' || rateValue <= 0}
            loading={saveRate.isPending}
            onClick={() => saveRate.mutate(Number(rateValue))}
          >
            עדכן
          </Button>
        </Group>
      </Card>

      <Card withBorder padding="md">
        <Title order={5} mb="xs">ברירות מחדל ל-BOM חדש</Title>
        <Text size="xs" c="dimmed" mb="sm">
          חלים על BOM חדש בלבד; BOM קיים שומר על הערכים שלו.
        </Text>
        <Group align="flex-end">
          <NumberInput label='מע"מ %' hideControls min={0} max={100} decimalScale={2} value={vat} onChange={(v) => setVat(typeof v === 'number' ? v : '')} disabled={!isAdmin} style={{ maxWidth: 120 }} />
          <NumberInput label="משלוח %" hideControls min={0} max={100} decimalScale={2} value={ship} onChange={(v) => setShip(typeof v === 'number' ? v : '')} disabled={!isAdmin} style={{ maxWidth: 120 }} />
          <Button
            disabled={!isAdmin}
            loading={saveDefaults.isPending}
            onClick={() => saveDefaults.mutate({ vat_pct: Number(vat) || 0, shipping_pct: Number(ship) || 0 })}
          >
            שמור
          </Button>
        </Group>
      </Card>
    </SimpleGrid>
  )
}

function MasterDataTab() {
  const { isAdmin } = useAuth()
  const [importing, setImporting] = useState(false)

  return (
    <Stack>
      <Group justify="flex-end">
        <Button variant="light" onClick={() => setImporting(true)}>ייבוא קטלוג</Button>
      </Group>
      <SimpleGrid cols={{ base: 1, md: 3 }}>
        <MasterDataLists disabled={!isAdmin} />
        <ManufacturerAliases disabled={!isAdmin} />
      </SimpleGrid>
      <CatalogImportModal opened={importing} onClose={() => setImporting(false)} />
    </Stack>
  )
}

function DealersTab() {
  const qc = useQueryClient()
  const { canEdit, canDelete } = useAuth()
  // `include_archived` on purpose: without it, archiving a dealer removes it
  // from the only screen that could bring it back.
  const dealers = useQuery({
    queryKey: ['dealers', 'all'],
    queryFn: () => api.get<Dealer[]>('/dealers?include_archived=true'),
  })
  const [editing, setEditing] = useState<Dealer | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Partial<Dealer>>({})
  const [pricesFor, setPricesFor] = useState<Dealer | null>(null)

  const save = useMutation({
    mutationFn: (d: Partial<Dealer>) =>
      d.id ? api.put(`/dealers/${d.id}`, d) : api.post('/dealers', d),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dealers'] })
      void qc.invalidateQueries({ queryKey: ['master-data'] })
      notifications.show({ message: 'הספק נשמר', color: 'green' })
      setEditing(undefined)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'השמירה נכשלה.'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.delete<unknown>(`/dealers/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dealers'] })
      void qc.invalidateQueries({ queryKey: ['master-data'] })
      notifications.show({ message: 'הספק נמחק', color: 'green' })
    },
    // Refused while any BOM or price list references the dealer, and the
    // message says how many and offers ARCHIVED instead — which is the right
    // answer for a retired supplier, since deleting one would orphan its
    // orders. Worth showing as written.
    onError: (err) => notifications.show({
      color: 'red',
      message: err instanceof ApiError ? err.message : 'המחיקה נכשלה',
    }),
  })

  const columns = useMemo<ColumnDef<Dealer, any>[]>(
    () => [
      { accessorKey: 'name', header: 'שם ספק' },
      { accessorKey: 'default_currency', header: 'מטבע', meta: { hideBelow: 'md' } },
      { accessorKey: 'status', header: 'סטטוס', cell: (c) => <StatusBadge value={c.getValue<string>()} /> },
      { accessorKey: 'notes', header: 'הערות', meta: { hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            {canDelete && (
              <>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setPricesFor(c.row.original)}
                >
                  מחירון
                </Button>
                <ActionIcon
                  size={30} color="red" variant="subtle" aria-label="מחק ספק"
                  loading={remove.isPending && remove.variables === c.row.original.id}
                  onClick={() => {
                    void confirmDelete(`הספק "${c.row.original.name}"`).then(
                      (ok) => ok && remove.mutate(c.row.original.id!))
                  }}
                >
                  ✕
                </ActionIcon>
              </>
            )}
          </Group>
        ),
      },
    ],
    [canDelete, remove],
  )

  return (
    <Stack style={FILL_REMAINING}>
      <Group justify="flex-end">
        {canEdit && (
          <Button onClick={() => { setDraft({ name: '', default_currency: 'USD', status: 'ACTIVE' }); setError(null); setEditing(null) }}>
            ספק חדש
          </Button>
        )}
      </Group>
      <Paper withBorder style={FILL_PANEL}>
        <DataGrid
          data={dealers.data ?? []}
          columns={columns}
          loading={dealers.isLoading}
          empty="אין ספקים"
          onRowClick={(d) => { if (canEdit) { setDraft({ ...d }); setError(null); setEditing(d) } }}
          maxHeight="100%"
        />
      </Paper>

      <DealerPricesModal dealer={pricesFor} onClose={() => setPricesFor(null)} />

      <Modal opened={editing !== undefined} onClose={() => setEditing(undefined)} title={editing ? 'עריכת ספק' : 'ספק חדש'}>
        <form onSubmit={(e) => {
          e.preventDefault()
          if (!draft.name?.trim()) return setError('יש להזין שם ספק.')
          save.mutate(draft)
        }}>
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <TextInput label="שם" required value={draft.name ?? ''} onChange={(e) => {
            const v = e.currentTarget.value
            setDraft((d) => ({ ...d, name: v }))
          }} />
          <Select label="מטבע ברירת מחדל" data={['USD', 'ILS']} value={draft.default_currency ?? 'USD'} onChange={(v) => setDraft((d) => ({ ...d, default_currency: v ?? 'USD' }))} />
          {/* Uppercase, and not a free-text field: `DealerDAO.get_all` filters
              on `status='ACTIVE'` exactly, so a dealer saved as "active"
              vanishes from every active-only list while still looking fine in
              the table. */}
          <Select
            label="סטטוס"
            data={[{ value: 'ACTIVE', label: 'פעיל' }, { value: 'ARCHIVED', label: 'בארכיון' }]}
            value={draft.status ?? 'ACTIVE'}
            onChange={(v) => setDraft((d) => ({ ...d, status: v ?? 'ACTIVE' }))}
          />
          <TextInput label="הערות" value={draft.notes ?? ''} onChange={(e) => {
            const v = e.currentTarget.value
            setDraft((d) => ({ ...d, notes: v }))
          }} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(undefined)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמור</Button>
          </Group>
        </Stack>
        </form>
      </Modal>
    </Stack>
  )
}

/**
 * How many prices a dealer quotes, and the way to clear them.
 *
 * Deliberately a count and not a table. `GET /dealers/{id}/prices` is keyed by
 * **component id** and carries no SKU, so rendering the rows would show a
 * column of integers that identify nothing — the join that would make them
 * readable belongs on the server, and until it exists a count is the honest
 * answer to "does this dealer have a price list?".
 *
 * Wiping is the operation that actually matters: a dealer price file is
 * re-imported wholesale, and a stale row for a SKU the new file dropped keeps
 * pricing BOMs at a price the dealer no longer offers. Clearing first is how
 * the desktop's re-import is meant to be done.
 */
function DealerPricesModal({
  dealer, onClose,
}: { dealer: Dealer | null; onClose: () => void }) {
  const qc = useQueryClient()
  const { isViewer } = useAuth()
  const opened = dealer !== null

  // The caller (`DealersTab`) only ever opens this modal from a
  // `canDelete`-gated button, which already excludes a viewer — but
  // `GET /dealers/{id}/prices` is a 403 for one regardless (DP-01: the whole
  // response *is* pricing, nothing partial to redact), so the query is kept
  // from firing here too rather than relying solely on that button staying
  // gated.
  const prices = useQuery({
    queryKey: ['dealer-prices', dealer?.id],
    queryFn: () => api.get<Record<string, unknown>>(`/dealers/${dealer!.id}/prices`),
    enabled: opened && !isViewer,
  })

  const wipe = useMutation({
    mutationFn: () => api.delete<{ count: number }>(`/dealers/${dealer!.id}/prices`),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['dealer-prices'] })
      void qc.invalidateQueries({ queryKey: ['items'] })
      notifications.show({ message: `${res.count} מחירים נמחקו`, color: 'green' })
      onClose()
    },
    onError: (err) => notifications.show({
      color: 'red',
      message: err instanceof ApiError ? err.message : 'המחיקה נכשלה',
    }),
  })

  const count = Object.keys(prices.data ?? {}).length

  return (
    <Modal opened={opened} onClose={onClose} title={`מחירון — ${dealer?.name ?? ''}`}>
      <Stack>
        {isViewer ? (
          // Matches the server's own wording for this 403 (DP-01) — the
          // dealer's price list is not a number a viewer is missing, it is a
          // screen they are not shown.
          <Text size="sm" c="dimmed">אין הרשאה לצפייה במחירי ספק.</Text>
        ) : prices.isLoading ? (
          <Text size="sm" c="dimmed">טוען…</Text>
        ) : (
          <Text>
            הספק מתמחר <b>{count.toLocaleString('he-IL')}</b> רכיבים.
          </Text>
        )}
        <Text size="xs" c="dimmed">
          מחירי ספק נוצרים בייבוא קובץ מחירון דרך נתוני אב → ייבוא קטלוג.
          מחיקת המחירון לפני ייבוא מחדש מונעת שורות ישנות עבור מק"טים שכבר
          אינם בקובץ — הן ימשיכו לתמחר BOM במחיר שהספק כבר לא נותן.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>סגור</Button>
          <Button
            color="red"
            disabled={!count || isViewer}
            loading={wipe.isPending}
            onClick={() => {
              void confirmDelete(`כל ${count} המחירים של "${dealer?.name}"`).then(
                (ok) => ok && wipe.mutate())
            }}
          >
            מחק מחירון
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function UsersTab() {
  const qc = useQueryClient()
  const { user: me } = useAuth()
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') })
  const [creating, setCreating] = useState(false)
  const [resetting, setResetting] = useState<User | null>(null)
  const [draft, setDraft] = useState({ username: '', password: '', role: 'viewer' })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.post<User>('/users', draft),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ message: 'המשתמש נוצר', color: 'green' })
      setCreating(false)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'היצירה נכשלה.'),
  })

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) =>
      api.patch<User>(`/users/${id}/role`, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ message: 'התפקיד עודכן — הסשנים של המשתמש בוטלו', color: 'green' })
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'העדכון נכשל' }),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ message: 'המשתמש נמחק', color: 'green' })
    },
    onError: (err) => notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'המחיקה נכשלה' }),
  })

  function submitNewUser() {
    if (!draft.username.trim()) return setError('יש להזין שם משתמש.')
    const problem = passwordProblem(draft.password)
    if (problem) return setError(problem)
    create.mutate()
  }

  const columns = useMemo<ColumnDef<User, any>[]>(
    () => [
      { accessorKey: 'username', header: 'שם משתמש' },
      {
        accessorKey: 'role',
        header: 'תפקיד',
        cell: (c) => (
          <Select
            size="xs"
            w={110}
            data={[
              { value: 'admin', label: 'מנהל' },
              { value: 'editor', label: 'עורך' },
              { value: 'viewer', label: 'צופה' },
            ]}
            value={c.getValue<string>()}
            // Disabled rather than spinning: a Select cannot show a pending
            // state, and a second change while the first is in flight is a
            // race over one user's role.
            disabled={changeRole.isPending &&
                      changeRole.variables?.id === c.row.original.id}
            onChange={(v) => v && changeRole.mutate({ id: c.row.original.id, role: v })}
          />
        ),
      },
      { accessorKey: 'created_at', header: 'נוצר', meta: { hideBelow: 'lg' }, cell: (c) => fmtDateTime(c.getValue<string>()) },
      { accessorKey: 'last_login', header: 'כניסה אחרונה', meta: { hideBelow: 'md' }, cell: (c) => fmtDateTime(c.getValue<string>()) },
      {
        id: 'act',
        header: '',
        meta: { nowrap: true, actions: true },
        cell: (c) => (
          <Group gap={4} wrap="nowrap">
            {c.row.original.id === me?.id ? (
              // Your own password goes through חשבון → שינוי סיסמה in the
              // header, which requires the current one. Resetting your own
              // password here would let anyone holding your session set a new
              // one without proving they are you.
              <Badge size="xs" variant="light">אני</Badge>
            ) : (
              <>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setResetting(c.row.original)}
                >
                  אפס סיסמה
                </Button>
                <ActionIcon
                  size={30} color="red" variant="subtle" aria-label="מחק משתמש"
                  loading={remove.isPending && remove.variables === c.row.original.id}
                  onClick={() => {
                    void confirmDelete(`המשתמש "${c.row.original.username}"`).then(
                      (ok) => ok && remove.mutate(c.row.original.id))
                  }}
                >
                  ✕
                </ActionIcon>
              </>
            )}
          </Group>
        ),
      },
    ],
    [changeRole, remove, me],
  )

  return (
    <Stack style={FILL_REMAINING}>
      <Group justify="flex-end">
        <Button onClick={() => { setDraft({ username: '', password: '', role: 'viewer' }); setError(null); setCreating(true) }}>
          משתמש חדש
        </Button>
      </Group>
      <Text size="xs" c="dimmed">
        שינוי תפקיד או איפוס סיסמה מבטלים מיד את כל הסשנים של אותו משתמש.
      </Text>
      <Paper withBorder style={FILL_PANEL}>
        <DataGrid data={users.data ?? []} columns={columns} loading={users.isLoading} empty="אין משתמשים" maxHeight="100%" />
      </Paper>

      <Modal opened={creating} onClose={() => setCreating(false)} title="משתמש חדש">
        <form onSubmit={(e) => { e.preventDefault(); submitNewUser() }}>
        <Stack>
          {error && <Alert color="red" variant="light">{error}</Alert>}
          <TextInput label="שם משתמש" required value={draft.username} onChange={(e) => {
            const v = e.currentTarget.value
            setDraft((d) => ({ ...d, username: v }))
          }} />
          <PasswordInput
            label="סיסמה"
            required
            description={`לפחות ${MIN_PASSWORD_LENGTH} תווים, וכולל ספרה או סימן`}
            value={draft.password}
            onChange={(e) => {
              const v = e.currentTarget.value
              setDraft((d) => ({ ...d, password: v }))
            }}
          />
          <Select
            label="תפקיד"
            data={[
              { value: 'admin', label: `מנהל — ${ROLE_LABEL_HE.admin}` },
              { value: 'editor', label: 'עורך — הוספה ועריכה' },
              { value: 'viewer', label: 'צופה — קריאה בלבד' },
            ]}
            value={draft.role}
            onChange={(v) => setDraft((d) => ({ ...d, role: v ?? 'viewer' }))}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCreating(false)}>ביטול</Button>
            <Button type="submit" loading={create.isPending}>צור</Button>
          </Group>
        </Stack>
        </form>
      </Modal>

      <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />
    </Stack>
  )
}

function DataTab() {
  const [busy, setBusy] = useState(false)
  const version = useQuery({
    queryKey: ['schema-version'],
    queryFn: () => api.get<{ version: number; expected: number }>('/settings/schema-version'),
  })
  const backups = useQuery({
    queryKey: ['backups'],
    queryFn: () => api.get<Backup[]>('/settings/backups'),
  })

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Card withBorder padding="md">
        <Title order={5} mb="xs">גיבוי מסד הנתונים</Title>
        <Text size="xs" c="dimmed" mb="sm">
          מוריד עותק עקבי של מסד הנתונים. העותק נוצר דרך מנגנון הגיבוי של
          SQLite ולא כהעתקה של הקובץ החי, כדי שלא ייווצר קובץ פגום.
        </Text>
        <Button
          loading={busy}
          onClick={async () => {
            setBusy(true)
            try {
              await api.download('/settings/backup', 'procurement-backup.db')
              notifications.show({ message: 'הגיבוי הורד', color: 'green' })
            } catch (err) {
              notifications.show({
                color: 'red',
                message: err instanceof ApiError ? err.message : 'הגיבוי נכשל',
              })
            } finally {
              setBusy(false)
            }
          }}
        >
          הורד גיבוי
        </Button>
      </Card>

      <Card withBorder padding="md">
        <Title order={5} mb="xs">גרסת סכמה</Title>
        <Group gap="xs">
          <Badge variant="light">v{version.data?.version ?? '…'}</Badge>
          <Text size="sm" c="dimmed">צפוי: v{version.data?.expected ?? '…'}</Text>
        </Group>
        {version.data && version.data.version !== version.data.expected && (
          <Alert color="yellow" variant="light" mt="sm">
            גרסת הסכמה במסד אינה תואמת את גרסת התוכנה.
          </Alert>
        )}
      </Card>

      <Card withBorder padding="md">
        <Title order={5} mb="xs">גיבויים אוטומטיים</Title>
        {/* Deliberately blunt. These files sit beside the database inside the
            container: they survive a restart, not a lost volume. Calling them
            "backups" without saying that reads as an assurance nobody checked. */}
        <Text size="xs" c="dimmed" mb="sm">
          נשמרים לצד מסד הנתונים בתוך הקונטיינר, ושורדים הפעלה מחדש — אך לא
          אובדן של ה-volume. הגיבוי האמיתי הוא גיבוי ה-volume ב-NAS.
        </Text>
        {backups.isLoading && <Text size="sm" c="dimmed">טוען…</Text>}
        {backups.data?.length === 0 && (
          <Text size="sm" c="dimmed">אין גיבויים אוטומטיים עדיין.</Text>
        )}
        {!!backups.data?.length && (
          <Stack gap={4}>
            {backups.data.map((b) => (
              <Group key={b.name} justify="space-between" wrap="nowrap">
                <Text size="sm" style={CODE_STYLE}>{b.name}</Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {fmtDateTime(b.modified)} · {(b.size_bytes / 1_048_576).toFixed(1)} MB
                </Text>
              </Group>
            ))}
          </Stack>
        )}
        <Text size="xs" c="dimmed" mt="sm">
          שחזור נעשה מהשרת בעזרת <code>scripts/restore.sh</code> ולא מהדפדפן —
          ראה docs/operations/SCRIPTS.md.
        </Text>
      </Card>
    </SimpleGrid>
  )
}

function AuditTab() {
  const log = useQuery({
    queryKey: ['audit-log'],
    queryFn: () => api.get<AuditRow[]>('/settings/audit-log?limit=300'),
  })

  const columns = useMemo<ColumnDef<AuditRow, any>[]>(
    () => [
      { accessorKey: 'created_at', header: 'מתי', cell: (c) => fmtDateTime(c.getValue<string>()) },
      { accessorKey: 'actor', header: 'מי' },
      { accessorKey: 'action', header: 'פעולה', meta: { code: true } },
      { accessorKey: 'entity', header: 'ישות', meta: { hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'entity_id', header: 'מזהה', meta: { code: true, hideBelow: 'lg' }, cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'details', header: 'פרטים', meta: { hideBelow: 'md' }, cell: (c) => c.getValue<string>() || EM_DASH },
    ],
    [],
  )

  return (
    <Paper withBorder style={FILL_PANEL}>
      <DataGrid data={log.data ?? []} columns={columns} loading={log.isLoading} empty="היומן ריק" maxHeight="100%" />
    </Paper>
  )
}
